import * as THREE from "three";
import type { FootState, Skater } from "../skater";
import { FOOT_BACK, FOOT_RUN } from "../sim/tuning";
import { solveLeg } from "./ik";
import type { Leg } from "./skater-parts";

// The rider on foot (#19): the walk cycle, the blend between the board pose
// and walking, and the board carried under the arm. `SkaterModel` poses the
// rider on the board first and this blends the walk over it.

/**
 * The walk cycle (#122) follows the sim's steps (`FootState.steps`, so
 * footsteps land with the feet), and opens up from a walk to a run: how far
 * the legs swing through a step (rad), how high the swing leg's knee comes
 * up (rad), how far the arms swing against the legs (rad), and how far the
 * hips dip as each foot comes down (m).
 */
const SWING_WALK = 0.45;
const SWING_RUN = 0.72;
const KNEE_WALK = 0.75;
const KNEE_RUN = 1.35;
const ARM_WALK = 0.45;
const ARM_RUN = 0.9;
const BOB_WALK = 0.03;
const BOB_RUN = 0.06;
/** 0 at a walk (`FOOT_BACK` or slower), 1 at a full run. */
const runShare = (speed: number): number => Math.min(1, Math.max(0, (Math.abs(speed) - FOOT_BACK) / (FOOT_RUN - FOOT_BACK)));
const mix = (walk: number, run: number, r: number): number => walk + (run - walk) * r;
/** How fast the rider blends between the board and walking (1/s). */
const FOOT_BLEND_RATE = 9;

/** The parts of the rider the walk poses. */
export interface WalkRig {
  rider: THREE.Group;
  hips: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  legs: Leg[];
}

export class WalkPose {
  /** 0..1 how much of a step the legs are swinging through, eased so stopping settles. */
  amp = 0;
  /** 0 at a walk, 1 at a full run: how far the stride opens up, eased so walking into a wall doesn't snap it. */
  run = 0;
  /** 0 on the board, 1 on foot, eased so stepping off and back on blend the legs, body and board across. */
  blend = 0;
  /** 0..1 knees tucked in an on-foot jump, eased. */
  private tuck = 0;
  /** The last on-foot state, so the stride keeps going while blending back onto the board. */
  private lastFoot: FootState = { speed: 0, vy: 0, airborne: false, steps: 0, runOut: 0, ledge: null };
  /** Every part the walk moves, in a fixed order, and where the board pose left each one (reused each frame). */
  private parts: THREE.Object3D[];
  private fromP: THREE.Vector3[];
  private fromQ: THREE.Quaternion[];

  constructor(private rig: WalkRig) {
    this.parts = [rig.rider, rig.hips, rig.torso, rig.head, ...rig.legs.flatMap((l) => [l.hip, l.knee, l.foot])];
    this.fromP = this.parts.map(() => new THREE.Vector3());
    this.fromQ = this.parts.map(() => new THREE.Quaternion());
  }

  /** Eases the stride, the blend and the jump's tuck toward this frame's state; called after posing. */
  ease(sk: Skater, dt: number): void {
    const pace = sk.foot && !sk.foot.airborne ? Math.min(1, Math.abs(sk.foot.speed) / 3) : 0;
    this.amp += (pace - this.amp) * (1 - Math.exp(-8 * dt));
    if (sk.foot) this.run += (runShare(sk.foot.speed) - this.run) * (1 - Math.exp(-8 * dt));
    this.blend += ((sk.foot ? 1 : 0) - this.blend) * (1 - Math.exp(-FOOT_BLEND_RATE * dt));
    if (Math.abs(this.blend - (sk.foot ? 1 : 0)) < 1e-3) this.blend = sk.foot ? 1 : 0;
    if (sk.foot) Object.assign(this.lastFoot, sk.foot);
    this.tuck += ((sk.foot?.airborne || (sk.foot?.ledge?.climb ?? -1) >= 0 ? 1 : 0) - this.tuck) * (1 - Math.exp(-12 * dt));
  }

  /** The free arm's swing against its side's leg while walking, further at a run. */
  armSwing(foot: FootState): number {
    return mix(ARM_WALK, ARM_RUN, this.run) * this.amp * Math.sin(Math.PI * foot.steps);
  }

  /**
   * On foot, or blending off or back onto the board: the walk's pose,
   * blended in from wherever the board pose (body and board, feet included)
   * left everything.
   */
  apply(sk: Skater, hipY: number): void {
    const b = this.blend;
    if (b <= 0) return;
    if (b < 1) {
      this.parts.forEach((o, i) => {
        this.fromP[i]!.copy(o.position);
        this.fromQ[i]!.copy(o.quaternion);
      });
    }
    this.walk(sk.foot ?? this.lastFoot, hipY);
    if (b < 1) {
      this.parts.forEach((o, i) => {
        o.position.lerpVectors(this.fromP[i]!, TMP_P.copy(o.position), b);
        o.quaternion.slerpQuaternions(this.fromQ[i]!, TMP_Q.copy(o.quaternion), b);
      });
    }
  }

  /**
   * Carried on edge under the back arm, nose forward, grip against the
   * rider; part way there from the ground while blending. With anything
   * else out of the pack (the gun, #136; the washer or a ride walked beside
   * you, #153; `packed` 0..1) it's strapped nose-up on the backpack instead,
   * leaving both hands free.
   */
  carryBoard(board: THREE.Object3D, boardSpin: THREE.Object3D, side: number, packed = 0): void {
    const b = this.blend;
    const p = packed * b;
    TMP_P.set(side * 0.3, 0.8, 0.05).lerp(TMP_P2.set(0, 1.0, -0.3), p);
    board.position.lerp(TMP_P, b);
    boardSpin.rotation.set(-(Math.PI / 2 - 0.15) * p, 0, side * (Math.PI / 2 - 0.12) * b * (1 - p));
  }

  /**
   * The rider turns to face where it's going, off the board and down on the
   * ground, head up, and the legs swing through a step, longer at a run:
   * the leg coming through bends its knee, the planted one straightens, and
   * the hips dip as each foot comes down. Tucked up in a jump or climbing a
   * ledge.
   */
  private walk(foot: FootState, standHipY: number): void {
    const { rider, hips, torso, head, legs } = this.rig;
    const phase = Math.PI * foot.steps;
    const a = this.amp;
    const r = this.run;
    rider.position.set(0, 0, 0);
    rider.rotation.set(0, Math.PI / 2, 0, "ZYX");
    // Lowest with the legs spread as a foot comes down (half a step in, `cues.ts`), highest passing under the hips.
    const hipY = standHipY - mix(BOB_WALK, BOB_RUN, r) * a * Math.abs(Math.sin(phase)) - 0.12 * this.tuck;
    hips.position.set(0, hipY, 0);
    torso.rotation.set(0, 0, 0.06 + (0.1 + 0.08 * r) * a, "YZX");
    head.rotation.set(0, 0, -0.05 - 0.08 * a, "ZYX");
    legs.forEach((leg, i) => {
      // Feet under the hips, not spread along a board.
      solveLeg(leg, hipY, leg.hip.position.z * 1.2 - leg.z);
      const swing = mix(SWING_WALK, SWING_RUN, r) * a * Math.sin(phase + i * Math.PI);
      // Forward is -x in the rider's frame, which is a negative turn about z.
      leg.hip.rotation.z -= swing;
      // The leg coming through bends its knee to lift its foot, straightening to plant it; a jump tucks both.
      const lift = Math.max(0, Math.cos(phase + i * Math.PI)) * mix(KNEE_WALK, KNEE_RUN, r) * a + 0.5 * this.tuck;
      leg.hip.rotation.z -= 0.5 * lift;
      leg.knee.rotation.z += lift;
      leg.foot.rotation.z -= 0.5 * lift;
    });
  }
}

const TMP_P = new THREE.Vector3();
const TMP_P2 = new THREE.Vector3();
const TMP_Q = new THREE.Quaternion();
